export const COMMUNITY_AMBASSADOR_FEATURE_FLAG = 'CommunityAmbassadors';

export const SAVE_STATUS_IDLE = 'idle' as const;
export const SAVE_STATUS_SAVING = 'saving' as const;
export const SAVE_STATUS_SAVED = 'saved' as const;
export const SAVE_STATUS_ERROR = 'error' as const;
export type SaveStatus =
  | typeof SAVE_STATUS_IDLE
  | typeof SAVE_STATUS_SAVING
  | typeof SAVE_STATUS_SAVED
  | typeof SAVE_STATUS_ERROR;

export const TRACK_ATTENDANCE_BULK_UPLOAD_SINGLE_SHIFT = 'TrackAttendanceBulkUploadSingleShift';
export const TRACK_ATTENDANCE_BULK_UPLOAD_MULTI_SHIFT = 'TrackAttendanceBulkUploadMultiShift';
export const EVENT_CREATION = 'EventCreation';
export const MANAGE_SIGNUPS_ON_EVENT_LEVEL = 'ManageSignupsOnEventLevel';
export const ADMIN_EVENT_PAGE_ACCESS = 'AdminEventPageAccess';
export const AI_CONTENT_ASSIST = 'AiContentAssist';
export const SHIFT_WAITLIST = 'ShiftWaitlist';
// Gates the volunteer shift table on the Search results and My Activity Upcoming/Past pages only
// (cce-7126). Separate from SHIFT_WAITLIST so those two surfaces roll out on their own schedule;
// waitlist behaviour inside the table stays on SHIFT_WAITLIST.
export const SEARCH_AND_ACTIVITY_SHIFT_TABLE = 'SearchAndActivityShiftTable';
// Fired once per landing for each server-enabled feature flag the session is dialed into. Value is
// the flag name (e.g. 'NavigationRedesign'). Lets us segment engagement/conversion by dial-up cohort.
export const FEATURE_FLAG_LOAD_METRIC = 'FeatureFlagLoad';
// Signup-click A/B metric. Value is `${cohort}:${surface}`, e.g. 'treatment:search'.
export const SIGNUP_COHORT_METRIC = 'shifts_signup_cohort';
// Denominator mirror of SIGNUP_COHORT_METRIC: emitted once per signup-capable surface view, keyed with
// the same `${cohort}:${surface}` value, so signup click-through = SIGNUP_COHORT_METRIC / this metric.
export const SIGNUP_EXPOSURE_METRIC = 'shifts_signup_exposure_cohort';
export const SIGNUP_COHORT_TREATMENT = 'treatment';
export const SIGNUP_COHORT_CONTROL = 'control';
export const SIGNUP_SURFACE_SEARCH = 'search';
export const SIGNUP_SURFACE_ACTIVITY = 'activity';
export const SIGNUP_SURFACE_EVENT_DETAILS = 'event-details';
export type SignupSurface =
  | typeof SIGNUP_SURFACE_SEARCH
  | typeof SIGNUP_SURFACE_ACTIVITY
  | typeof SIGNUP_SURFACE_EVENT_DETAILS;
export const COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL = 'CoordinatorSignupNotificationsInternal';
export const NAVIGATION_REDESIGN = 'NavigationRedesign';
// Gates the Volunteer summary on /my-activity (cce-4530). Runs on mock data until the
// GetVolunteerActivitySummary API ships (cce-8087).
export const MY_ACTIVITY_SUMMARY = 'MyActivitySummary';
export const COORDINATOR_SIGNUP_NOTIFICATIONS_EXTERNAL = 'CoordinatorSignupNotificationsExternal';
export const LOCALIZATION = 'Localization';

// constants used for website analytics metrics key
export const UNDER_SCORE = '_';
export const UNAUTHORIZED = 'unauthorizedUserAction';
export const COORDINATOR_EVENT_SHIFTS_TABLE = 'coordinatorEventShiftsTable';
export const EVENT_DETAIL_PAGE = 'eventDetailPage';
export const COORDINATOR_ACTIVITY_PAGE = 'coordinatorActivityPage';

export const COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE =
  EVENT_DETAIL_PAGE + UNDER_SCORE + COORDINATOR_EVENT_SHIFTS_TABLE;
export const COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE =
  COORDINATOR_ACTIVITY_PAGE + UNDER_SCORE + COORDINATOR_EVENT_SHIFTS_TABLE;

export const COORDINATOR_EVENT_SHIFTS_LIST = 'coordinatorEventShiftsList';
export const COORDINATOR_EVENT_LIST = 'coordinatorEventList';
export const ACTIVITY_PAGE = 'activityPage';
export const COORDINATOR_ACTIVITY_DASHBOARD_LINK = 'coordinatorActivity_dashboardLink';
export const SIGN_UP_ACTION = 'signUpAction';
export const SEARCH_RESULTS = 'eventSearch';
export const TRACK_ATTENDANCE_ACTION = 'trackAttendanceAction';
export const TRACK_UNLISTED_EVENT_ACTION = 'trackUnlistedEventAction';
export const CREATE_EVENT_ACTION = 'createEventAction';

export const SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE =
  COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE + UNDER_SCORE + SIGN_UP_ACTION;
export const SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE =
  COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE + UNDER_SCORE + SIGN_UP_ACTION;
export const TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE =
  COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE + UNDER_SCORE + TRACK_ATTENDANCE_ACTION;
export const TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE =
  COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE +
  UNDER_SCORE +
  TRACK_ATTENDANCE_ACTION;

export const TRACK_UNLISTED_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE =
  COORDINATOR_ACTIVITY_PAGE + UNDER_SCORE + TRACK_UNLISTED_EVENT_ACTION;

export const CREATE_EVENT_FROM_COORDINATOR_ACTIVITY_PAGE =
  COORDINATOR_ACTIVITY_PAGE + UNDER_SCORE + CREATE_EVENT_ACTION;

// Signup management analytics
export const SIGNUP_MANAGEMENT = 'signupManagement';
export const SINGLE_SHIFT_VIEW = 'singleShiftView';
export const SIGNUP_MANAGEMENT_PAGE_VIEW = SIGNUP_MANAGEMENT + '_pageView';

// Signup management for a single shift
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_PAGE_VIEW =
  SIGNUP_MANAGEMENT_PAGE_VIEW + '_' + SINGLE_SHIFT_VIEW;
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT = SIGNUP_MANAGEMENT + '_' + SINGLE_SHIFT_VIEW;
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_AMAZONIANS_BUTTON =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_signupAmazoniansButton';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_CANCEL_SIGNUP =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_cancelSignup';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_SEARCH_PEOPLE_FILTER =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_searchPeopleFilter';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_ADD_SIGNUP_FROM_SEARCH =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_addSignupFromSearch';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATE_SIGNUPS =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_bulkUploadValidateSignups';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SUCCESS_COUNT =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_bulkUploadValidationSuccessCount';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_ERRORS_COUNT =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_bulkUploadValidationErrorsCount';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_VALIDATION_SKIPPED_COUNT =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_bulkUploadValidationSkippedCount';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_BULK_UPLOAD_ADD_SIGNUPS =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_bulkUploadAddSignups';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_DOWNLOAD_SIGNUP_LIST =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_downloadButton';
export const SIGNUP_MANAGEMENT_SINGLE_SHIFT_SIGNUP_THROUGH_GIVE_HUB_LINK =
  SIGNUP_MANAGEMENT_SINGLE_SHIFT + '_manageSignupsFromGiveHub';

// Waitlist analytics (cce-7138). Volunteer-facing events use the `volunteerWaitlist` prefix;
// coordinator-facing events (waitlist management + opt-out during event creation/edit) use
// `coordinatorWaitlist`.
export const VOLUNTEER_WAITLIST = 'volunteerWaitlist';
export const COORDINATOR_WAITLIST = 'coordinatorWaitlist';

// Page views
export const VOLUNTEER_WAITLIST_PAGE_VIEW = VOLUNTEER_WAITLIST + '_pageView';
export const COORDINATOR_WAITLIST_PAGE_VIEW = COORDINATOR_WAITLIST + '_pageView';

// Volunteer > Join Waitlist
export const VOLUNTEER_WAITLIST_JOIN_CLICK_SIMPLE_SIGNUP =
  VOLUNTEER_WAITLIST + '_joinClick_simpleSignup';
export const VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP_MODAL =
  VOLUNTEER_WAITLIST + '_joinClick_complexSignupModal';
export const VOLUNTEER_WAITLIST_JOIN_CLICK_COMPLEX_SIGNUP =
  VOLUNTEER_WAITLIST + '_joinClick_complexSignup';

// Volunteer > Leave Waitlist
export const VOLUNTEER_WAITLIST_LEAVE_MODAL = VOLUNTEER_WAITLIST + '_leaveModal';
export const VOLUNTEER_WAITLIST_LEAVE_CONFIRM = VOLUNTEER_WAITLIST + '_leaveConfirm';

// Coordinator > Waitlist Management (addSpotAndSignup variants intentionally omitted — not enabled yet)
export const COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST =
  COORDINATOR_WAITLIST + '_removeFromWaitlist';
export const COORDINATOR_WAITLIST_REMOVE_FROM_WAITLIST_BULK =
  COORDINATOR_WAITLIST + '_removeFromWaitlistBulk';

// Coordinator > Waitlist Opt-Out (event creation & edit)
export const COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_ON = COORDINATOR_WAITLIST + '_optOut_toggleOn';
export const COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_OFF = COORDINATOR_WAITLIST + '_optOut_toggleOff';
export const COORDINATOR_WAITLIST_OPT_OUT_CLEAR_WAITLIST_MODAL =
  COORDINATOR_WAITLIST + '_optOut_clearWaitlistModal';
// The bundle of translated strings RedVelvetTranslations compiles for this website.
export const WEBSITE_COMMON_BUNDLE = 'website.common';
export const WEBSITE_RICH_TEXT_EDITOR_BUNDLE = 'website.richTextEditor';
export const WEBSITE_ERRORS_BUNDLE = 'website.errors';

// Language picker. Opening the dropdown and choosing a language are tracked separately, so choices
// are not over-counted.
export const LANGUAGE_PICKER = 'languagePicker';
export const LANGUAGE_PICKER_DROPDOWN = LANGUAGE_PICKER + '_dropdown';
export const LANGUAGE_PICKER_SELECT = LANGUAGE_PICKER + '_select';
export const CHANGE_LANGUAGE_NAV_ITEM_ID = 'changeLanguage';
// Shared by the side nav and the top nav's support menu, which both offer the translation report:
// the top nav derives its click metric from the item id, so a second spelling would split the count.
export const SUGGEST_TRANSLATION_FIX_NAV_ITEM_ID = 'suggestTranslationFix';
// Must stay in step with the matching selector in App.scss.
export const LANGUAGE_PICKER_CONTROL_ID = 'vc-language-picker';
// Snake case, unlike the metrics above, because localization reporting spans the website, the API,
// and emails, and this name is what those dashboards already query.
export const SESSION_LOCALE_METRIC = 'vc_session_locale';
// What decided that language, so sessions can be split by users who chose one, users whose profile
// named one, users matched from their browser, users left on the default, and users the feature is
// still switched off for.
export const SESSION_LOCALE_SOURCE_METRIC = 'vc_session_locale_source';
// The language the browser asked for, raw, so we can see which languages people want that VC has yet
// to be translated into.
export const SESSION_BROWSER_LOCALE_METRIC = 'vc_session_browser_locale';
// A bundle the website could not assemble at all, by bundle name, so every string it carries renders
// blank. Narrower than it sounds: a pack the browser cannot fetch still yields a bundle, because
// arb-tools carries on to the `und` root, so a network outage or a missing static asset lands on
// TRANSLATION_UNUSABLE_MESSAGE_METRIC per string rather than here. Deliberately not the Lambda's
// TranslationPackLoadFailure, which is the email manifest: one name for both would send whoever is
// reading the dashboard to the wrong half of the system.
export const TRANSLATION_PACK_LOAD_FAILURE_METRIC = 'vc_translation_pack_load_failure';
// A string the website showed nothing for, as `bundle/id`. Covers an id a pack carries but cannot
// format, every id on a pack that failed to download, and an id no pack carries at all — that last
// one only where a single reader was asked, since a caller asking several bundles in turn treats a
// bundle without the id as the ordinary case and stays silent.
export const TRANSLATION_UNUSABLE_MESSAGE_METRIC = 'vc_translation_unusable_message';

//  Ambassadors
export const AMBASSADOR_EXTERNAL_PLAN_EVENT_LINK =
  'https://w.amazon.com/bin/view/AmazonInTheCommunity/Volunteering/PlanAnEvent/';
export const MIN_PER_HOUR = 60;
export const MAX_SHIFT_HOURS = 12;
export const SHORT_SHIFT_HOURS = 8;
export const SHORT_SHIFT_ALLOWED_EXTRA_HOURS = 2;
